import React, { useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { fetchAllPhotoSpots } from '../../lib/api'
import { applyPhotoFilters } from '../../lib/photoFilters'
import { readSessionJson, writeSessionJson } from '../../utils/storage'
import { shuffle } from '../../lib/utils'
import { PHOTO_CATEGORIES } from 'alakai-aloha-shared'
import PhotoSpotsFilterBar from '../../components/photo/PhotoSpotsFilterBar'
import PhotoSpotsGallery from '../../components/photo/PhotoSpotsGallery'
import PhotoSpotsMapView from '../../components/photo/PhotoSpotsMapView'
import PhotoSpotsCollage from '../../components/photo/PhotoSpotsCollage'
import PhotoSpotsLoadMoreButton from '../../components/photo/PhotoSpotsLoadMoreButton'
import { getRuntimeSiteContext } from '../../lib/siteContext'
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls'
import PhotoSpotPreviewModal from '../../components/photo/PhotoSpotPreviewModal'

const STORAGE_KEY_PREFIX = 'alakai_photo_planner_state_v2'
const PAGE_SIZE = 30

export default function PhotoPlanner() {
  const { island, islandConfig } = useMemo(() => getRuntimeSiteContext(), [])
  const storageKey = useMemo(() => `${STORAGE_KEY_PREFIX}_${island}`, [island])
  const defaultFilters = useMemo(
    () => ({
      island,
      region: [],
      categories: [],
      searchText: ''
    }),
    [island]
  )
  const [spots, setSpots] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const stored = useMemo(() => readSessionJson(storageKey), [storageKey])
  const sanitizeCategories = (values) =>
    Array.isArray(values) ? values.filter((value) => PHOTO_CATEGORIES.includes(value)) : []

  const [filters, setFilters] = useState(() => ({
    ...defaultFilters,
    island: defaultFilters.island,
    region: stored?.filters?.region || defaultFilters.region,
    categories: sanitizeCategories(stored?.filters?.categories),
    searchText: stored?.filters?.searchText || defaultFilters.searchText
  }))
  const [mapBounds, setMapBounds] = useState(null)
  const mapViewRef = React.useRef(stored?.mapView ?? null)
  const [currentPage, setCurrentPage] = useState(1)
  const [activeIndex, setActiveIndex] = useState(0)
  const [autoLoading, setAutoLoading] = useState(false)
  const [selectedSpot, setSelectedSpot] = useState(null)

  useEffect(() => {
    writeSessionJson(storageKey, {
      filters,
      mapView: mapViewRef.current,
      storedAt: Date.now()
    })
  }, [filters, storageKey])

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1)
    setActiveIndex(0)
  }, [filters])

  useEffect(() => {
    let mounted = true
    setLoading(true)
    fetchAllPhotoSpots()
      .then((data) => {
        if (!mounted) return
        const list = Array.isArray(data) ? data : []
        // Randomize initial order so the default “no filters” view feels exploratory.
        setSpots(shuffle(list))
        setError(null)
      })
      .catch((e) => {
        if (!mounted) return
        setError(e)
      })
      .finally(() => {
        if (!mounted) return
        setLoading(false)
      })

    return () => {
      mounted = false
    }
  }, [])

  const filtered = useMemo(() => applyPhotoFilters(spots, filters), [spots, filters])

  const inViewport = useMemo(() => {
    if (!mapBounds) return filtered
    return filtered.filter((spot) => {
      const { lat, lng } = spot.location || {}
      if (!lat || !lng) return false
      return mapBounds.contains({ lat, lng })
    })
  }, [filtered, mapBounds])

  const displayed = useMemo(
    () => inViewport.slice(0, currentPage * PAGE_SIZE),
    [inViewport, currentPage]
  )

  useEffect(() => {
    if (displayed.length === 0) {
      setActiveIndex(0)
      return
    }
    if (activeIndex > displayed.length - 1) {
      setActiveIndex(0)
    }
  }, [displayed, activeIndex])

  const activeSpot = displayed[activeIndex] || displayed[0] || null

  useEffect(() => {
    if (!autoLoading) return undefined
    if (displayed.length >= inViewport.length) return undefined
    const sentinel = document.querySelector('[data-photo-load-more]')
    if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          setCurrentPage((prev) => {
            const nextPage = prev + 1
            return nextPage
          })
        })
      },
      { rootMargin: '200px 0px 200px 0px', threshold: 0.1 }
    )
    observer.observe(sentinel)

    return () => observer.disconnect()
  }, [autoLoading, displayed.length, inViewport.length])

  return (
    <div className="photo-planner-index-page pt-0">
      <Helmet>
        <title>Photo Planner | Alaka'i Aloha</title>
        {island !== 'kauai' ? <meta name="robots" content="noindex" /> : null}
        <meta
          name="description"
          content={`Plan sunrise, sunset, and other photo moments on ${islandConfig.title} with a map-based photo spot planner.`}
        />
        <link rel="canonical" href={buildRuntimeCanonicalUrl('/photo-planner')} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4 space-y-6">
        <div className="flex flex-col gap-2 mb-2 sm:flex-row sm:justify-between sm:items-start">
          <div className="space-y-1">
            <h1 className="text-4xl font-bold">Photo Planner</h1>
            <p className="text-sm sm:text-base text-white/70 max-w-3xl">
              Built for travelers who care about documenting their trip with great photos. Browse our favorite images
              from around the island, then click any shot for exact locations and tips to recreate it. Use the
              interactive map to scout nearby spots or narrow your search geographically.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-white/80">Loading photo spots…</div>
        ) : error ? (
          <div className="text-red-300">
            Failed to load photo spots. Check Sanity schema deployment and content.
          </div>
        ) : (
          <>
            <PhotoSpotsFilterBar filters={filters} onChange={setFilters} island={island} />

            <div className="mt-6">
              <PhotoSpotsGallery
                spots={displayed}
                activeIndex={activeIndex}
                onActiveIndexChange={setActiveIndex}
              />
            </div>

            <div className="mt-6">
              <PhotoSpotsMapView
                spots={displayed}
                onBoundsChanged={setMapBounds}
                initialCenter={mapViewRef.current?.center}
                initialZoom={mapViewRef.current?.zoom}
                island={island}
                highlightedSpotId={activeSpot?._id}
                onMarkerSelect={(spot) => {
                  const nextIndex = displayed.findIndex((item) => item._id === spot._id)
                  if (nextIndex >= 0) setActiveIndex(nextIndex)
                  setSelectedSpot(spot)
                }}
                onViewChanged={(nextView) => {
                  const prevView = mapViewRef.current
                  mapViewRef.current = nextView
                  writeSessionJson(storageKey, {
                    filters,
                    mapView: nextView,
                    storedAt: Date.now()
                  })
                  // Reset to the first page when the user changes map view (pan/zoom),
                  // so the list becomes a “random 30 within the viewport” experience.
                  if (prevView) {
                    const zoomChanged = prevView.zoom !== nextView.zoom
                    const latChanged =
                      Math.abs((prevView.center?.lat || 0) - (nextView.center?.lat || 0)) > 1e-6
                    const lngChanged =
                      Math.abs((prevView.center?.lng || 0) - (nextView.center?.lng || 0)) > 1e-6
                    if (zoomChanged || latChanged || lngChanged) setCurrentPage(1)
                  }
                }}
              />
              <div className="text-[11px] sm:text-xs text-white/60 mt-2 text-center sm:text-left">
                Pin locations are approximate and based on our best estimate.
              </div>
            </div>

            <div className="mt-8">
              <PhotoSpotsCollage spots={displayed} />
            </div>

            {displayed.length < inViewport.length ? (
              <>
                {!autoLoading ? (
                  <PhotoSpotsLoadMoreButton onClick={() => {
                    setAutoLoading(true)
                    setCurrentPage((p) => p + 1)
                  }} />
                ) : null}
                <div data-photo-load-more className="h-8" />
              </>
            ) : null}

            {selectedSpot ? (
              <PhotoSpotPreviewModal spot={selectedSpot} onClose={() => setSelectedSpot(null)} />
            ) : null}

            <div className="text-sm text-white/70 text-center pt-8">
              Have a photo we should add? Email{' '}
              <a href="mailto:photos@alakaialoha.com" className="text-emerald-300 hover:text-emerald-200">
                photos@alakaialoha.com
              </a>
              .
            </div>
          </>
        )}
      </div>
    </div>
  )
}
